import React, { useState, useEffect, useCallback } from 'react';
import { fetchLinkedEmails, fileEmailToMatter, fileEmailAttachments } from '../api';

export default function LinkedEmails({ authAxios, caseId, caseNumber, clientEmail, onToast }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [filingId, setFilingId] = useState(null);
  const [filingResults, setFilingResults] = useState({});

  const loadEmails = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await fetchLinkedEmails(authAxios, caseId, search);
      setEmails(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 400) {
        onToast?.(err.response.data?.detail || 'Cannot fetch emails.', 'danger');
      } else {
        console.error('Failed to load linked emails', err);
        onToast?.('Failed to load emails.', 'danger');
      }
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, search, onToast]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const handleFileEmail = useCallback(async (email) => {
    setFilingId(email.id);
    try {
      // File the email body as a CommunicationLog
      await fileEmailToMatter(authAxios, caseId, {
        message_id: email.id,
        user_email: clientEmail,
        subject: email.subject,
        body: email.body_content || email.body_preview,
        sender_name: email.from_name,
        sender_email: email.from_email,
        received_date: email.received_date,
      });

      // If email has attachments, file them too
      let attachmentResult = null;
      if (email.has_attachments) {
        try {
          attachmentResult = await fileEmailAttachments(authAxios, caseId, {
            message_id: email.id,
            user_email: clientEmail,
            email_subject: email.subject,
            email_date: email.received_date,
          });
        } catch (attErr) {
          // Attachment filing may 409 if already filed by the email step — that's OK
          if (attErr?.response?.status !== 409) {
            console.error('Attachment filing error', attErr);
          }
        }
      }

      // Mark as filed locally
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_filed: true } : e));
      if (attachmentResult?.filed) {
        setFilingResults(prev => ({ ...prev, [email.id]: attachmentResult.filed }));
      }
      onToast?.('Email filed to matter.', 'success');
    } catch (err) {
      if (err?.response?.status === 409) {
        setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_filed: true } : e));
        onToast?.('Email was already filed.', 'info');
      } else {
        console.error('Failed to file email', err);
        onToast?.('Failed to file email.', 'danger');
      }
    } finally {
      setFilingId(null);
    }
  }, [authAxios, caseId, clientEmail, onToast]);

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '—' : dt.toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search emails by subject..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={loadEmails}
          disabled={loading}
          className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {!clientEmail && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
          <i className="bi bi-exclamation-triangle mr-2"></i>
          Client has no email address on file. Email linking is unavailable.
        </div>
      )}

      {loading && <div className="text-sm text-gray-500 py-4 text-center">Loading emails...</div>}

      {!loading && emails.length === 0 && clientEmail && (
        <div className="text-sm text-gray-500 py-4 text-center">
          No emails found. Ensure Microsoft Graph integration is configured in Firm Settings.
        </div>
      )}

      {/* Email list */}
      <div className="space-y-2">
        {emails.map(email => {
          const isExpanded = expandedId === email.id;
          const isFiling = filingId === email.id;
          const results = filingResults[email.id];

          return (
            <div key={email.id} className="bg-white border border-gray-200 rounded-lg shadow-sm">
              {/* Email header row */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedId(isExpanded ? null : email.id)}
              >
                <i className={`bi ${email.is_filed ? 'bi-envelope-check text-green-600' : 'bi-envelope text-gray-400'}`}></i>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{email.subject || '(No subject)'}</span>
                    {email.has_attachments && (
                      <i className="bi bi-paperclip text-gray-400" title="Has attachments"></i>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {email.from_name || email.from_email} &middot; {formatDate(email.received_date)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {email.is_filed ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                      <i className="bi bi-check-circle mr-1"></i>Filed
                    </span>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleFileEmail(email); }}
                      disabled={isFiling}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isFiling ? (
                        <>
                          <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full mr-1"></span>
                          Filing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-check mr-1"></i>File to Matter
                        </>
                      )}
                    </button>
                  )}
                  <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} text-gray-400`}></i>
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {email.body_preview || '(No preview available)'}
                  </div>

                  {/* Filing results */}
                  {results && results.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <div className="text-xs font-medium text-gray-500 uppercase">Filed Attachments</div>
                      {results.map((r, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <i className="bi bi-file-earmark text-gray-400"></i>
                          <span className="text-gray-700">{r.name}</span>
                          {r.action === 'new_document' ? (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">New Document</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                              New Version{r.matched_title ? ` of ${r.matched_title}` : ''} (v{r.version_number})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
